// Vorschau von Medien und Folien. Folien werden mit @disploraq/layouts gerendert – derselbe Code wie im Player.
import { embeddedLayoutIds, renderLayout, type LayoutV1, type LayoutV2, type WeatherProvider, type ZoneContentMap } from '@disploraq/layouts';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { usePrayerPreviewTables } from '../api/prayer';
import { imageUrl, useDataSets, usedSources } from '../api/datasources';
import { managerWeather } from '../api/weather';
import { useLayouts, useMedia, usePlaylist } from '../api/queries';
import type { LayoutRow, Media, PlaylistItem } from '../api/types';
import { localeOf } from '../i18n';

export const mediaUrl = (m: Pick<Media, 'id' | 'current'>) => (m.current ? `/api/v1/media/${m.id}/versions/${m.current.id}/preview` : '');

export interface MediaIndex {
  list: Media[];
  byId: Map<string, Media>;
  bySha: Map<string, Media>;
  resolveAsset: (sha: string) => string;
  assetKind: (sha: string) => 'image' | 'video' | undefined;
}

export function useMediaIndex(): MediaIndex {
  const { data } = useMedia();
  return useMemo(() => {
    const list = data ?? [];
    const byId = new Map(list.map((m) => [m.id, m]));
    const bySha = new Map(list.flatMap((m) => (m.current ? [[m.current.sha256, m] as const] : [])));
    return {
      list, byId, bySha,
      resolveAsset: (sha) => {
        const m = bySha.get(sha);
        return m ? mediaUrl(m) : '';
      },
      assetKind: (sha) => bySha.get(sha)?.kind,
    };
  }, [data]);
}

/** Baustein „Vorlage": Definitionen eingebetteter Vorlagen (Liste nur laden, wenn die Folie welche enthält). */
function useEmbeds(definition: unknown, given: LayoutRow[] | undefined) {
  const needed = useMemo(() => embeddedLayoutIds(definition).length > 0, [definition]);
  const { data } = useLayouts({ enabled: needed && !given });
  const list = needed ? (given ?? data) : undefined;
  return useMemo(() => (list ? (id: string) => list.find((l) => l.id === id)?.definition as LayoutV1 | LayoutV2 | undefined : undefined), [list]);
}

export function LayoutPreview({ definition, content, media, live = false, weather = managerWeather, layouts, layoutId }: {
  definition: unknown; content?: unknown; media: MediaIndex; live?: boolean;
  /** Wetterdaten (Galerie: Beispielwetter, damit Vorlagen vollständig aussehen); sonst echtes Wetter über den Server. */
  weather?: WeatherProvider;
  /** Vorlagen für eingebettete Vorlagen (sonst aus der API). */
  layouts?: LayoutRow[];
  /** id der gezeigten Vorlage (Schutz vor Selbsteinbettung). */
  layoutId?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const layout = useEmbeds(definition, layouts);
  // Datenquellen (Feed, Termine, Tabelle, Feldbelegung): echte Daten vom Server
  const ids = useMemo(() => usedSources(definition, content), [definition, content]);
  const data = useDataSets(ids);
  const key = JSON.stringify([definition, content, data.version]);
  // Gebetszeiten: Zeitpläne der Bausteine nachladen (im Player kommen sie aus dem Manifest)
  const prayerTables = usePrayerPreviewTables((definition as { elements?: LayoutV2['elements'] } | null)?.elements);
  useEffect(() => {
    const el = ref.current;
    if (!el || !definition || typeof definition !== 'object') return;
    try {
      return renderLayout(el, definition as LayoutV1 | LayoutV2, {
        preview: !live,
        resolveAsset: (sha) => media.resolveAsset(sha) || (data.images.has(sha) ? imageUrl(sha) : ''),
        data: data.get,
        assetKind: media.assetKind,
        content: (content ?? {}) as ZoneContentMap,
        locale: localeOf(i18n.language),
        weather,
        ...(prayerTables.length ? { prayerTables } : {}),
        ...(layout ? { layout } : {}),
        ...(layoutId ? { layoutId } : {}),
      });
    } catch {
      return undefined;
    }
  }, [key, media, i18n.language, weather, prayerTables, layout, layoutId]);
  return <div ref={ref} className="wl-canvas" style={{ position: 'absolute', inset: 0 }} />;
}

export function MediaView({ media, controls = false }: { media: Media | undefined; controls?: boolean }) {
  const { t } = useTranslation();
  if (!media) return <span>{t('media.missing')}</span>;
  if (!media.current) return <span>{t(`media.status.uploading`)}</span>;
  // Erst nach der Prüfung (Virenscan, Umwandlung) gibt es Vorschau-Dateien
  if (media.current.status !== 'ready') return <span>{t(`media.status.${media.current.status}`)}</span>;
  const thumb = media.current.thumbnailUrl;
  // Kacheln: kleines Thumbnail statt der ganzen Datei (Videos: Standbild)
  if (!controls && thumb) return <img src={thumb} alt={media.name} loading="lazy" />;
  return media.kind === 'image'
    ? <img src={mediaUrl(media)} alt={media.name} loading="lazy" />
    : <video src={`${mediaUrl(media)}#t=1`} muted preload="metadata" controls={controls} aria-label={media.name} poster={media.current.posterUrl ?? undefined} />;
}

export function ItemThumb({ item, media, layouts }: { item: Pick<PlaylistItem, 'kind' | 'mediaAssetId' | 'layoutId' | 'layoutContent'> | undefined; media: MediaIndex; layouts: LayoutRow[] | undefined }) {
  const { t } = useTranslation();
  if (!item) return <span>{t('playlists.emptyThumb')}</span>;
  if (item.kind === 'media') return <MediaView media={media.byId.get(item.mediaAssetId ?? '')} />;
  const l = layouts?.find((x) => x.id === item.layoutId);
  if (!l) return <span>{t('playlists.slide')}</span>;
  return <LayoutPreview definition={l.definition} content={item.layoutContent} media={media} />;
}

/** Erste Folie einer Playlist (für Bildschirm-Kacheln). */
export function PlaylistThumb({ playlistId }: { playlistId: string }) {
  const { data } = usePlaylist(playlistId);
  const media = useMediaIndex();
  const { data: layouts } = useLayouts();
  const first = data?.items.find((i) => i.enabled);
  return first ? <ItemThumb item={first} media={media} layouts={layouts} /> : null;
}
